import { signinSchema } from "@frontend-shared/schema/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useRegistrationStatus } from "@/api/auth/useRegistrationStatus";
import { useSignin } from "@/api/auth/useSignin";
import { FormSubmitButton } from "@/components/FormSubmitButton";
import { StyledInput } from "@/components/StyledInput";
import { StyledPasswordInput } from "@/components/StyledPasswordInput";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { PATHS } from "@/constants/paths";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useOnPremise } from "@/hooks/use-on-premise";
import { getDesktopBridge } from "@/lib/desktop";
import { getDocsReturnPath } from "@/lib/docs-session";
import { clearLocalStorage, cn } from "@/lib/utils";
import { FORM_VALIDATION_OPTIONS } from "@/lib/validations/form-defaults";

import { AuthHeader } from "./AuthHeader";
import { AuthLayout } from "./AuthLayout";
import { ChangeTenantDialog } from "./ChangeTenantDialog";
import { AUTH_CONTROL_HEIGHT_CLASS } from "./constants/auth-controls";
import { SocialButtons } from "./SocialButtons";

const SignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOnPremise } = useOnPremise();
  const { t } = useTranslation("auth");
  useDocumentTitle(t("page_title"));
  const signInMutation = useSignin();
  const { data: registrationStatus } = useRegistrationStatus();
  const [isChangeTenantOpen, setIsChangeTenantOpen] = useState<boolean>(false);
  const desktopBridge = getDesktopBridge();

  useEffect(() => {
    clearLocalStorage();
  }, []);

  const form = useForm({
    ...FORM_VALIDATION_OPTIONS,
    defaultValues: {
      email: "",
      password: "",
    },
    resolver: zodResolver(signinSchema(t)),
  });

  const handleSubmit = form.handleSubmit(async (values) => {
    signInMutation.mutate(values, {
      onSuccess: () => {
        // NOTE: NSAI-4426 — a visitor bounced here by the docs proxy (a docs
        // link opened while signed out) came for that docs page. Staying on
        // this URL lets NonProtectedRoute run the docs_session handoff to it
        // as soon as the user loads — the same single path an
        // already-authenticated visitor takes — instead of racing it to /chat.
        if (getDocsReturnPath(location.search)) return;

        const returnTo =
          typeof location.state?.from === "string"
            ? location.state.from
            : PATHS.CHAT;
        navigate(returnTo, { replace: true });
      },
    });
  });

  return (
    <AuthLayout>
      <div className="flex flex-col gap-4">
        <AuthHeader description={t("sign_in.description")} className="mb-2" />
        <SocialButtons registrationStatus={registrationStatus} />
        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field, fieldState: { error } }) => (
                <FormItem>
                  <FormLabel>{t("email.label")}</FormLabel>
                  <FormControl>
                    <StyledInput
                      className={AUTH_CONTROL_HEIGHT_CLASS}
                      placeholder={t("email.placeholder")}
                      {...field}
                      isError={!!error}
                      data-testid="sign-in_email-input"
                    />
                  </FormControl>
                  <FormMessage data-testid="sign-in_email-error" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field, fieldState: { error } }) => (
                <FormItem>
                  <FormLabel>{t("password.label")}</FormLabel>
                  <FormControl>
                    <StyledPasswordInput
                      className={AUTH_CONTROL_HEIGHT_CLASS}
                      placeholder={t("password.placeholder")}
                      {...field}
                      autoComplete="current-password"
                      isError={!!error}
                      data-testid="sign-in_password-input"
                    />
                  </FormControl>
                  <FormMessage data-testid="sign-in_password-error" />
                  <Link
                    to="/auth/forgot-password"
                    className="text-sm/6 ms-auto text-link block w-fit"
                    data-testid="sign-in_forgot-password-link"
                  >
                    {t("sign_in.forgot_password")}
                  </Link>
                </FormItem>
              )}
            />
            <FormSubmitButton
              className={cn("w-full", AUTH_CONTROL_HEIGHT_CLASS)}
              isLoading={signInMutation.isPending}
              data-testid="sign-in_submit-button"
            >
              {t("sign_in.submit")}
            </FormSubmitButton>
          </form>
        </Form>
        <div className="flex flex-col gap-3">
          {registrationStatus?.registrationEnabled && (
            <p
              className="text-sm/6 text-center text-balance text-foreground-secondary"
              data-testid="sign-in_signup-content"
            >
              <Trans
                t={t}
                i18nKey="sign_in.no_account"
                components={{
                  SignUpLink: (
                    <Link
                      to="/auth/sign-up"
                      className="text-link"
                      data-testid="sign-in_signup-link"
                    />
                  ),
                }}
              />
            </p>
          )}

          {!isOnPremise && (
            <p className="text-sm/6 text-center text-balance text-foreground-secondary">
              <Trans
                t={t}
                i18nKey="terms"
                components={{
                  TermsLink: (
                    <Button className="p-0 m-0 text-link" variant="link" />
                  ),
                  PrivacyPolicyLink: (
                    <Button className="p-0 m-0 text-link" variant="link" />
                  ),
                }}
              />
            </p>
          )}

          {desktopBridge && (
            <div className="flex justify-center">
              <Button
                type="button"
                variant="link"
                className="text-sm text-foreground-secondary"
                onClick={() => setIsChangeTenantOpen(true)}
                data-testid="sign-in_change-tenant-button"
              >
                {t("change_tenant.button")}
              </Button>
            </div>
          )}
        </div>
      </div>
      {desktopBridge && (
        <ChangeTenantDialog
          isOpen={isChangeTenantOpen}
          onOpenChange={setIsChangeTenantOpen}
          bridge={desktopBridge}
        />
      )}
    </AuthLayout>
  );
};

export default SignIn;
